import { nowDateTimeLocalInTimeZone } from '../lib/timezones';

/**
 * Shared helpers for building the day / month / year date ranges used by the
 * reports and the expense category report. Dates are plain `YYYY-MM-DD`
 * strings so they can live in the URL and be compared lexicographically.
 */

/** Formats a Date as a local `YYYY-MM-DD` (never shifts the day via UTC). */
export function toInputDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

/** Parses a `YYYY-MM-DD` key into a Date whose local parts match that day. */
export function dateKeyToDate(dateKey: string): Date {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

/**
 * "Today" on the hospital's calendar.
 *
 * The browser may run in a different timezone than the hospital (Settings →
 * General), so `new Date()` is not good enough: around midnight it would seed
 * the report with the wrong day, month or year. The returned Date is a local
 * midnight whose components equal the hospital's calendar date, so every
 * existing `getFullYear/getMonth/getDate` helper keeps working unchanged.
 */
export function todayInTimeZone(timeZone?: string): Date {
  return dateKeyToDate(nowDateTimeLocalInTimeZone(timeZone).date);
}

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Inclusive `YYYY-MM-DD` bounds covering a full calendar month. */
export function monthBounds(year: number, month: number): { from: string; to: string } {
  return {
    from: toInputDate(new Date(year, month, 1)),
    to: toInputDate(new Date(year, month + 1, 0)),
  };
}

/** Inclusive `YYYY-MM-DD` bounds covering a full calendar year. */
export function yearBounds(year: number): { from: string; to: string } {
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

/** Inclusive `YYYY-MM-DD` bounds covering a single calendar day. */
export function dayBounds(date: Date = new Date()): { from: string; to: string } {
  const day = toInputDate(date);
  return { from: day, to: day };
}

export type QuickRange = { label: string; from: string; to: string };

/**
 * Ready-made "to date" ranges (Today, 7 Days, 30 Days, This Month, This Year)
 * used by the report quick-range buttons. Ranges end on `today` so they always
 * include the current day.
 */
export function quickRanges(today: Date = new Date()): QuickRange[] {
  return [
    { label: 'Today', ...dayBounds(today) },
    { label: '7 Days', from: toInputDate(new Date(today.getTime() - 6 * 86400000)), to: toInputDate(today) },
    { label: '30 Days', from: toInputDate(new Date(today.getTime() - 29 * 86400000)), to: toInputDate(today) },
    {
      label: 'This Month',
      from: toInputDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: toInputDate(today),
    },
    { label: 'This Year', from: toInputDate(new Date(today.getFullYear(), 0, 1)), to: toInputDate(today) },
  ];
}

export type PeriodSelection = { month: number | 'all' | 'custom'; year: number };

/**
 * Derives the Month/Year dropdown selection from a From/To pair:
 * a full month, a full year (`'all'`), or `'custom'` when the range matches
 * neither.
 */
export function derivePeriod(from: string, to: string): PeriodSelection {
  const year = Number(from.slice(0, 4)) || new Date().getFullYear();
  if (!from || !to) return { month: 'custom', year };
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  if (fy === ty && fm === tm && fd === 1 && td === new Date(fy, fm, 0).getDate()) {
    return { month: fm - 1, year: fy };
  }
  if (fy === ty && fm === 1 && fd === 1 && tm === 12 && td === 31) {
    return { month: 'all', year: fy };
  }
  return { month: 'custom', year };
}
